import React, { useEffect } from 'react';

/**
 * The operations console's vocabulary, defined once each.
 *
 * The companion to `portal.tsx`, which does the same job for the educator and
 * family environments. Every component here wraps a class name the operations
 * prototype defines, and that indirection is the whole point: `className="kpi"`
 * typed into six screens is six places to fix when the prototype renames it,
 * and six chances for one of them to be missed and quietly render unstyled.
 *
 * `portal-primitives.test.ts` fails the build if a screen reaches for one of
 * these classes directly.
 *
 * Nothing here invents a style. The CSS is the prototype's, generated and
 * scoped by scripts/build-redesign-css.js from
 * docs/redesign/IQMaxx/4_Admin_Operaciones.html.
 */

/* ------------------------------------------------------------------ page */

interface OpsPageProps {
  title: string;
  /** The line under the title — a date, a count, a period. */
  subtitle?: React.ReactNode;
  /** The screen's one primary action, right-aligned. */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The `.hd` block every operations screen opens with, and its content.
 *
 * Distinct from the portal's `.welcome`: the console's header is denser and
 * carries an action rather than a filter row, because an operator arrives at a
 * screen to do one thing.
 */
export const OpsPage: React.FC<OpsPageProps> = ({ title, subtitle, action, children }) => (
  <>
    <div className="hd">
      <div>
        <div className="h1">{title}</div>
        {subtitle ? <div className="hsub">{subtitle}</div> : null}
      </div>
      {action}
    </div>
    {children}
  </>
);

/* ------------------------------------------------------------------- kpis */

/** The `.kpis` grid. Four across on desktop, two on tablet, one on phone. */
export const KpiRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="kpis">{children}</div>
);

type KpiTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'lav';

const TONE_VAR: Record<KpiTone, string | undefined> = {
  neutral: undefined,
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
  lav: 'var(--lav)',
};

interface KpiProps {
  label: string;
  value: React.ReactNode;
  /** The small line under the figure. */
  note?: string;
  /**
   * Semantic colour for the figure and its note.
   *
   * A token name rather than a colour, so a screen never writes a hex value.
   * Green, amber and red mean the same thing on every screen and do not change
   * with the console's accent — the prototype's own theme picker says so.
   */
  tone?: KpiTone;
  /** Draws attention to the tile itself, not just its figure. */
  alert?: boolean;
}

/** One `.kpi` tile. */
export const Kpi: React.FC<KpiProps> = ({ label, value, note, tone = 'neutral', alert }) => {
  const colour = TONE_VAR[tone];

  return (
    <div className={`kpi${alert ? ' alert' : ''}`}>
      <div className="l">{label}</div>
      <b style={colour ? { color: colour } : undefined}>{value}</b>
      {note ? (
        <div
          className={`d${tone === 'neutral' ? ' mut' : ''}`}
          style={colour ? { color: colour } : undefined}
        >
          {note}
        </div>
      ) : null}
    </div>
  );
};

/* ----------------------------------------------------------------- tables */

interface TableProps {
  /**
   * The grid track list, e.g. `88px 1fr 130px 96px 90px`.
   *
   * Inline on each row, which is how the prototype does it and is right: this
   * is layout *data* that differs per table, not styling. Passing it once here
   * and applying it to every row is what keeps a header aligned with its body.
   */
  columns: string;
  /** Header cells, in order. */
  head: React.ReactNode[];
  children: React.ReactNode;
}

const ColumnsContext = React.createContext<string>('1fr');

/** The `.tbl` container, its `.hh` header row, and the rows beneath. */
export const Table: React.FC<TableProps> = ({ columns, head, children }) => (
  <ColumnsContext.Provider value={columns}>
    <div className="tbl">
      <div className="tr hh" style={{ gridTemplateColumns: columns }}>
        {head.map((cell, i) => (
          // Header cells are a fixed list defined at the call site; there is no
          // stable id to key by and reordering them is a code change.
          // eslint-disable-next-line react/no-array-index-key
          <span key={i}>{cell}</span>
        ))}
      </div>
      {children}
    </div>
  </ColumnsContext.Provider>
);

interface RowProps {
  /** When given, the whole row is activatable. */
  onOpen?: () => void;
  /** Names the row for a screen reader when it is activatable. */
  label?: string;
  children: React.ReactNode;
}

/**
 * One `.tr` row, taking its columns from the table above it.
 *
 * A clickable row is given a button role and a key handler rather than a bare
 * `onClick`: the prototype's rows are divs, and an operations console that
 * cannot be driven from the keyboard is one an experienced operator will be
 * slower in than the spreadsheet they were using before.
 */
export const Row: React.FC<RowProps> = ({ onOpen, label, children }) => {
  const columns = React.useContext(ColumnsContext);

  return (
    <div
      className={`tr${onOpen ? ' row' : ''}`}
      style={{ gridTemplateColumns: columns }}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? label : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  );
};

/* ------------------------------------------------------------------ pills */

export type PillTone = 'ok' | 'warn' | 'bad' | 'neu' | 'tint' | 'lav';

/** A `.pill` in one of the console's six tones. */
export const Pill: React.FC<{ tone?: PillTone; children: React.ReactNode }> = ({
  tone = 'neu',
  children,
}) => <span className={`pill p-${tone}`}>{children}</span>;

/**
 * The SLA tag — `.sla` in one of three states.
 *
 * One component decides overdue, due-soon and on-time for every surface that
 * shows one. The alternative is each screen doing its own arithmetic on
 * `hoursRemaining`, and two screens disagreeing about whether a case is late
 * is the kind of thing an operator notices and a test does not.
 *
 * The hours come from the server, which computed them from the record's
 * timestamp and the governed policy — ADM-02-FR-04 requires exactly that, and
 * a countdown run in the browser would not satisfy it. So does the due-soon
 * window (DEC-A02-07): no default here, or a second copy of the policy returns.
 * No hours at all means the case has no deadline, which is not the same as late.
 */
export const SlaTag: React.FC<{ hoursRemaining?: number; dueSoonHours: number }> = ({
  hoursRemaining,
  dueSoonHours,
}) => {
  if (hoursRemaining === undefined) {
    return <span className="sla ok">sin plazo</span>;
  }
  if (hoursRemaining < 0) {
    return <span className="sla late">{Math.abs(Math.round(hoursRemaining))}h de retraso</span>;
  }
  if (hoursRemaining <= dueSoonHours) {
    return <span className="sla soon">quedan {Math.round(hoursRemaining)}h</span>;
  }
  return <span className="sla ok">quedan {Math.round(hoursRemaining)}h</span>;
};

/* --------------------------------------------------------------- sections */

interface SectionProps {
  title: React.ReactNode;
  /** A count shown beside the title, as the prototype renders it. */
  count?: number;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}

/** A `.sec` block with its `.sech` heading. */
export const Section: React.FC<SectionProps> = ({ title, count, subtitle, action, children }) => (
  <div className="sec">
    <div className="sech">
      <div>
        <div className="sect">
          {title}
          {count !== undefined ? <span className="mut"> ({count})</span> : null}
        </div>
        {subtitle ? <div className="secs">{subtitle}</div> : null}
      </div>
      {action}
    </div>
    {children}
  </div>
);

/* ---------------------------------------------------------------- filters */

interface FilterBarProps<T extends string> {
  options: Array<{ value: T; label: React.ReactNode }>;
  value: T;
  onChange: (value: T) => void;
  /** Names the group for a screen reader, e.g. "Filtrar por acción". */
  label: string;
}

/** The `.fbar` row of `.fb` buttons, one of which is `.on`. */
export function FilterBar<T extends string>({
  options,
  value,
  onChange,
  label,
}: FilterBarProps<T>): React.ReactElement {
  return (
    <div className="fbar" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`fb${option.value === value ? ' on' : ''}`}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- fields */

/** A `.fld` label/value pair inside a drawer. */
export const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="fld">
    <div className="k">{label}</div>
    {children}
  </div>
);

/** The `.v` value line, for a plain fact. */
export const Value: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="v">{children}</div>
);

/** A `.note` panel. `bad` and `warn` carry the console's fixed status colours. */
export const Note: React.FC<{ tone?: 'plain' | 'warn' | 'bad'; children: React.ReactNode }> = ({
  tone = 'plain',
  children,
}) => <div className={`note${tone === 'plain' ? '' : ` ${tone}`}`}>{children}</div>;

/** The `.tl` step list — "What to do", numbered. */
export const Steps: React.FC<{ steps: string[] }> = ({ steps }) => (
  <div className="tl">
    {steps.map((step, i) => (
      // Steps are ordered prose with no identity of their own; the index is
      // the identity.
      // eslint-disable-next-line react/no-array-index-key
      <div className="tli" key={i}>
        <b>Paso {i + 1}</b>
        <span>{step}</span>
      </div>
    ))}
  </div>
);

/* ----------------------------------------------------------------- drawer */

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** The line under the title — an id, a course, a timestamp. */
  meta?: React.ReactNode;
  /** Buttons along the bottom. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The `.dv` side drawer, with its overlay.
 *
 * Escape closes it, which the prototype also does and which matters more here:
 * a drawer that traps an operator mid-case is the difference between a console
 * that feels fast and one that feels like a form.
 */
export const Drawer: React.FC<DrawerProps> = ({ open, onClose, title, meta, footer, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div className={`dv-ov${open ? ' show' : ''}`} onClick={onClose} aria-hidden="true" />
      <div
        className={`dv${open ? ' show' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
      >
        <div>
          <div className="dv-h">
            <div>
              <b>{title}</b>
              {meta ? <span>{meta}</span> : null}
            </div>
            <button className="dv-x" onClick={onClose} aria-label="Cerrar">
              ×
            </button>
          </div>
          <div className="dv-b">{children}</div>
          {footer ? <div className="dv-f">{footer}</div> : null}
        </div>
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ misc */

/** The `.empty` state. Says what is absent, not that something went wrong. */
export const Empty: React.FC<{ icon?: string; title: string; detail?: string }> = ({
  icon = '✅',
  title,
  detail,
}) => (
  <div className="empty">
    <div className="e" aria-hidden="true">
      {icon}
    </div>
    <b>{title}</b>
    {detail ? <span>{detail}</span> : null}
  </div>
);

/*
 * `FutureBanner` and `SoonChip` were written here and removed before this
 * landed. The uncalled-rules gate caught them as exported with no caller — the
 * screen that needs them is Roles & access, which the prototype itself marks
 * Future and which arrives in package 10. They return with it.
 */
